/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChange
} from '@angular/core';

import {take} from 'rxjs/operators';

import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';

import * as moment from 'moment/moment';

import Handsontable from 'handsontable';
import {colHeaders} from '../../+mass-data-update/time-attendance/table.settings';
import {columns} from '../../+mass-data-update/variable-payroll/table.settings';
import {User, UserService} from '../user';
import {isPresent} from '../utils/lang';
import {ColHeaderTooltipService} from './col-header-tooltip.service';

export const licenseKey = 'd453e-f313f-018a2-f4612-d7432';

const eventNames: Array<string> = [
  'afterCellMetaReset', 'afterChange', 'afterCreateCol', 'afterCreateRow',
  'afterDeselect', 'afterDestroy', 'afterDocumentKeyDown', 'afterGetCellMeta',
  'afterGetColHeader', 'afterGetRowHeader', 'afterInit', 'afterIsMultipleSelectionCheck',
  'afterLoadData', 'afterMomentumScroll', 'afterOnCellCornerMouseDown', 'afterOnCellMouseDown',
  'afterOnCellMouseOver', 'afterRemoveCol', 'afterRemoveRow', 'afterRender',
  'afterRenderer', 'afterScrollHorizontally', 'afterScrollVertically', 'afterSelection',
  'afterSelectionByProp', 'afterSelectionEnd', 'afterSelectionEndByProp', 'afterSetCellMeta',
  'afterUpdateSettings', 'afterValidate', 'beforeAutofill', 'beforeCellAlignment',
  'beforeChange', 'beforeChangeRender', 'beforeDrawBorders', 'beforeGetCellMeta',
  'beforeInit', 'beforeInitWalkontable', 'beforeKeyDown', 'beforeOnCellMouseDown',
  'beforeRemoveCol', 'beforeRemoveRow', 'beforeRender', 'beforeSetRangeEnd',
  'beforeTouchScroll', 'beforeValidate', 'construct', 'init', 'modifyColWidth', 'modifyRowHeight',
  'persistentStateLoad', 'persistentStateReset', 'persistentStateSave'
];

/**
 * Usage :
 * ```
 * <gp-hot-table [data]="data"
 *               [colHeaders]="colHeaders"
 *               [columns]="columns"
 *               [options]="options"
 *               [colWidths]="colWidths"
 *               [width]="width"
 *               (afterChange)="afterChange($event)">
 * </gp-hot-table>
 * ```
 *
 */
@Directive({
  selector: 'gp-hot-table',
  exportAs: 'gpHotTable',
  outputs: eventNames
})
export class HotTableDirective implements OnChanges, OnInit, OnDestroy {

  public table: any;
  private view: any;
  private exportPlugin: any;
  @Input() data: Array<any> = [];
  @Input() colHeaders;
  @Input() columns;
  @Input() colWidths;
  @Input() width;
  @Input() nestedHeaders;
  @Input() options: { [key: string]: any };
  @Input() tooltipHeaders: string[] = [];
  private column: number;
  private order: boolean;
  private cellChanges: any;
  private dateMap: Map<number, string[]>;

  @Output() private loadTableData = new EventEmitter<any>();

  private activeRowIndexes: Set<number> = new Set();

  constructor(private el: ElementRef,
              private userService: UserService,
              private renderer: Renderer2,
              private colHeaderTooltipService: ColHeaderTooltipService) {
    eventNames.forEach(eventName => this[eventName] = new EventEmitter<any>());
  }

  public ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (this.table) {
      const newSettings: { [key: string]: any } = {};
      if (changes.columns && changes.columns.currentValue !== changes.columns.previousValue) {
        newSettings['columns'] = changes['columns'].currentValue;
      }
      if (changes.colHeaders && changes.colHeaders.currentValue !== changes.colHeaders.previousValue) {
        newSettings.colHeaders = changes.colHeaders.currentValue;
      }
      if (changes.nestedHeaders && changes.nestedHeaders.currentValue !== changes.nestedHeaders.previousValue) {
        newSettings.nestedHeaders = changes.nestedHeaders.currentValue;
      }
      if (changes.data && !isEqual(changes.data.currentValue, changes.data.previousValue)) {
        newSettings.data = changes.data.currentValue;
      }
      if (changes.width && changes.width.currentValue !== changes.width.previousValue) {
        newSettings.width = changes['width'].currentValue;
      }
      if (Object.keys(newSettings).length) {
        this.table.updateSettings(newSettings);
        this.table.render();
      }

      if (changes.tooltipHeaders) {
        this.colHeaderTooltipService.clearAllTooltips(this.view);
        this.table.render();
      }

      if (changes.data && changes.data.currentValue.length) {
        setTimeout(() => {
          if (this.table.sort) {
            this.table.sort(this.column, this.order);
          }
          this.loadTableData.emit();
        }, 100);
      }
    }
  }

  public ngOnInit() {
    const that: any = this;

    this.view = this.renderer.createElement('div');
    this.renderer.appendChild(this.el.nativeElement, this.view);
    const htOptions: any = {data: this.data, licenseKey};
    eventNames.forEach(eventName => {
      if (eventName === 'afterGetColHeader') {
        htOptions[eventName] = (col: number, TH: HTMLTableCellElement) => {
          this[eventName].emit(col);
          this.colHeaderTooltipService.attachTooltip(col, TH, this.tooltipHeaders, this.table);
        };
      } else {
        htOptions[eventName] = data => {
          this[eventName].emit(data);
        };
      }
    });
    const additionalFields: Array<string> = ['colHeaders', 'colWidths', 'columns', 'width', 'nestedHeaders'];
    additionalFields.forEach(field => {
      if (this[field]) {
        Object.assign(htOptions, {
          [field]: this[field]
        });
      }
    });
    if (this.options) {
      Object.assign(htOptions, this.options);
    }
    this.table = new Handsontable(this.view, htOptions);
    this.exportPlugin = this.table.getPlugin('exportFile');
    if (this.options.selectedTab === 'time') {
      this.table.addHook('afterLoadData', () => {
        this.setCellMeta();
      });
      this.table.addHook('beforeChange', (changes: any) => {
        for (const change of changes) {
          change[3] = ('' + change[3]).replace(',', '.');
        }
      });
    } else if (this.options.selectedTab === 'variable') {
      let deleted: boolean = false;
      this.cellChanges = [];
      this.dateMap = new Map<number, string[]>();
      // memorize changed cells
      this.table.addHook('beforeChange', (changes, e) => {

        if (!changes) {
          return;
        }

        if (!e) {
          deleted = true;
        }

        for (const item of changes) {
          const rowIndex = item[0];
          const colIndex = columns.findIndex(col => col.data === item[1]);
          const previousValue = isPresent(item[2]) ? item[2] : '';
          const currentValue = isPresent(item[3]) ? item[3] : '';
          const index = this.table.getDataAtRow(rowIndex)[0];

          if (deleted) {
            this.cellChanges = this.cellChanges.filter((cell) => {
              return !(cell.rowIndex === rowIndex && cell.colIndex === colIndex);
            });
          }

          if (!deleted && currentValue !== previousValue) {
            this.cellChanges.push({rowIndex, colIndex, currentValue, index});
          }

          if (colIndex === 4 || colIndex === 5) {
            if (!this.dateMap.get(rowIndex)) {
              this.dateMap.set(rowIndex, []);
            }
            const rowMap = this.dateMap.get(rowIndex);
            rowMap[colIndex === 4 ? 0 : 1] = currentValue;
            rowMap[2] = index;
          }
        }

        setTimeout(() => {
          deleted = false;
        });
      });

      // set color to changed cells
      this.table.addHook('afterRender', () => {
        const cellChanges = this.cellChanges.filter((cellChange: any) => {
          return !!this.table.getData().find((tableItem: any, index) => {
            if (tableItem[0] === cellChange.index) {
              cellChange.rowIndex = index;
              return true;
            }
          });
        });

        for (const item of cellChanges) {
          const cell = that.table.getCell(item.rowIndex, item.colIndex);
          if (cell) {
            this.setCellColor(cell, 'green', 'white');
          }
        }

        this.dateMap.forEach((rowDates, rowIndex) => {
          if (!rowDates[0] || !rowDates[1]) {
            if (!rowDates[0] && that.table.getDataAtCell(rowIndex, 4)) {
              rowDates[0] = that.table.getDataAtCell(rowIndex, 4);
            } else if (!rowDates[1] && that.table.getDataAtCell(rowIndex, 5)) {
              rowDates[1] = that.table.getDataAtCell(rowIndex, 5);
            }
          }

          if (rowDates[0] && rowDates[1] && moment(rowDates[0]).isAfter(rowDates[1])) {
            const cellBefore = that.table.getCell(rowIndex, 4);
            const cellAfter = that.table.getCell(rowIndex, 5);
            this.setCellColor(cellBefore, 'red', 'white');
            this.setCellColor(cellAfter, 'red', 'white');
          }
        });
      });

      this.table.addHook('afterColumnSort', () => {
        this.setChangesIndex();
      });

      this.table.addHook('afterFilter', () => {
        this.setChangesIndex();
      });
    }
    this.table.addHook('afterColumnSort', (column: number, order: boolean) => {
      this.column = column;
      this.order = order;
      if (order === undefined) {
        this.column = undefined;
      }
    });

    // Add hooks for current-row class (multi-row support)
    this.table.addHook('afterSelection', (row: number, col: number, row2: number, col2: number) => {
      this.updateCurrentRowClassMulti(row, row2);
    });
    this.table.addHook('afterDeselect', () => {
      this.updateCurrentRowClassMulti(null, null);
    });
    this.table.addHook('afterRender', () => {
      this.reapplyCurrentRowClass();
    });
  }

  private updateCurrentRowClassMulti(rowIndex1: number, rowIndex2: number) {
    // Remove class from previously active rows
    if (this.activeRowIndexes.size && this.table) {
      this.activeRowIndexes.forEach(idx => {
        const prevRowCells = this.getRenderedRowCells(idx);
        if (prevRowCells) {
          for (let i = 0; i < prevRowCells.length; i++) {
            prevRowCells[i].classList.remove('current-row');
          }
        }
      });
    }
    this.activeRowIndexes.clear();
    // Add class to new selection
    if (rowIndex1 !== null && rowIndex2 !== null && this.table) {
      const start = Math.min(rowIndex1, rowIndex2);
      const end = Math.max(rowIndex1, rowIndex2);
      for (let r = start; r <= end; r++) {
        const rowCells = this.getRenderedRowCells(r);
        if (rowCells) {
          for (let i = 0; i < rowCells.length; i++) {
            rowCells[i].classList.add('current-row');
          }
        }
        this.activeRowIndexes.add(r);
      }
    }
  }

  private getRenderedRowCells(rowIndex: number): any {
    if (!this.table) return null;
    const colCount = (typeof this.table.countCols === 'function') ? this.table.countCols() : 50;
    for (let c = 0; c < colCount; c++) {
      const cell = this.table.getCell(rowIndex, c);
      if (cell && cell.parentNode && (cell.parentNode as any).children) {
        return (cell.parentNode as any).children;
      }
    }
    return null;
  }

  private reapplyCurrentRowClass() {
    if (!this.table) return;
    const rowCount = this.table.countRows();
    for (let r = 0; r < rowCount; r++) {
      const rowCells = this.getRenderedRowCells(r);
      if (rowCells) {
        for (let i = 0; i < rowCells.length; i++) {
          if (this.activeRowIndexes.has(r)) {
            rowCells[i].classList.add('current-row');
          } else {
            rowCells[i].classList.remove('current-row');
          }
        }
      }
    }
  }

  public setChangesIndex() {
    const ids = this.table.getDataAtProp(0);
    this.cellChanges = this.cellChanges.map((cell) => {
      ids.find((element, index) => {
        if (element === cell.index) {
          cell.rowIndex = index;
          return true;
        }
      });
      return cell;
    });

    this.dateMap.forEach((row, rowIndex) => {
      this.dateMap.set(rowIndex, []);
      ids.find((element, index) => {
        if (element === row[2]) {
          this.dateMap.set(index, row);
          return true;
        }
      });
    });
  }

  public setCellColor(cell: any, background: string, color: string): void {
    if (cell) {
      cell.style.backgroundColor = background;
      cell.style.color = color;
    }
  }


  public ngOnDestroy() {
    this.colHeaderTooltipService.clearAllTooltips(this.view);
    if (this.view) {
      this.view.remove();
    }
  }

  public exportAsFile(filename: string) {
    let csvSeparator: string = ',';
    this.userService.getUser().pipe(
      take(1)
    ).subscribe((user: User) => {
      if (user.decimalMask === ',') {
        csvSeparator = ';';
        this.formatNumbersForExport();
      }
      this.exportPlugin.downloadFile('csv', {
        filename: filename,
        columnHeaders: true,
        columnDelimiter: csvSeparator
      });
      if (user.decimalMask === ',') {
        this.exportPlugin.hot.loadData(this.data);
      }
    });
  }

  public formatNumbersForExport() {
    const exportData = cloneDeep(this.data);
    exportData.forEach(data => {
      Object.keys(data).forEach(key => {
        if (typeof data[key] === 'number') {
          data[key] = data[key].toString().replace('.', ',');
        }
      });
    });
    this.exportPlugin.hot.loadData(exportData);
  }

  public scrollViewportTo(row: number, column: number, snapToBottom: boolean = false, snapToRight: boolean = false) {
    if (this.table) {
      this.table.scrollViewportTo(row, column, snapToBottom, snapToRight);
    }
  }

  public clearChangeData() {
    this.cellChanges = [];
    this.dateMap = new Map<number, string[]>();
  }

  private setCellMeta() {
    this.data.forEach((row, rowIndex) => {
      Object.keys(row.expandedAbsences).forEach(key => {
        const absenceOptions: any = row.expandedAbsences[key];
        const cellProps: any = {readOnly: absenceOptions.readOnly, renderer: absenceOptions.renderer};
        this.table.setCellMetaObject(rowIndex, +key + colHeaders.length - 1, cellProps);
      });
    });
  }
}
